import { useTranslation } from 'react-i18next';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import {
  ControlledAutoComplete,
  ControlledTextField
} from 'components/shared/ControlledFields';
import { orderBy } from 'lodash';
import { useGetCountryCodeOptions } from 'hooks/useGetCountryCodeOptions';
import { countryCodeToOption } from 'components/admin-portal/Tables/TravelCosts/Distances/utils';
import { useEffect } from 'react';
import { LocationSectionProps } from '../types';

const LocationSection = ({
  control,
  setValue,
  rowData,
  isFieldHidden,
  watch
}: LocationSectionProps) => {
  const { t } = useTranslation('common');
  const hasPostalCode = !!watch?.('postalCode');
  const countryCodes = useGetCountryCodeOptions();

  useEffect(() => {
    if (rowData && countryCodes) {
      const defaultCountry = countryCodeToOption(
        rowData,
        countryCodes,
        'countryCode'
      );
      setValue('countryCode', defaultCountry);
    }
  }, [rowData, countryCodes, setValue]);

  return (
    <MultiStepForm.Section
      title={t('travelArrangements.location')}
      validateFields={
        isFieldHidden?.('location.cityAndStreet')
          ? ['countryCode', 'postalCode']
          : ['countryCode', 'postalCode', 'city', 'streetName', 'houseNumber']
      }
    >
      <ControlledAutoComplete
        name="countryCode"
        control={control}
        variant="outlined"
        label={t('country')}
        options={orderBy(countryCodes, ['label'], ['asc'])}
        idProperty="value"
        optionLabelProp="label"
        disableSorting
        fullWidth
      />

      <ControlledTextField
        name="postalCode"
        type="text"
        control={control}
        variant="outlined"
        label={t('postalCode')}
        InputLabelProps={{ shrink: hasPostalCode }}
      />
      {!isFieldHidden?.('location.cityAndStreet') && (
        <>
          <ControlledTextField
            name="city"
            type="text"
            control={control}
            variant="outlined"
            label={t('city')}
          />
          <ControlledTextField
            name="streetName"
            type="text"
            control={control}
            variant="outlined"
            label={t('street')}
          />

          <ControlledTextField
            name="houseNumber"
            type="text"
            control={control}
            variant="outlined"
            label={t('houseNumber')}
          />
        </>
      )}
    </MultiStepForm.Section>
  );
};

export default LocationSection;
